/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Mon Nov 20 2023
 **/

import { Column, Grid, Icon, Typography } from "@/frameworks/designStuff/components";
import { Drawer } from "@/frameworks/designStuff/compounds";
import { useGridResize, useIntersectionObserver } from "@/frameworks/libraries/hooks";
import { _P } from "@rfice/language";
import { FileCard } from "./FileCard";

interface SearchedFileProps {
	searchText: string;
	files: FileBox.Folders.GetAllFolderFileResponse["content"];
	onIntersecting: () => void;
}

export default function SearchedFile(props: SearchedFileProps) {
	const { targetRef, column } = useGridResize({ width: 216, gap: 10 });
	const { observerRef } = useIntersectionObserver<HTMLLIElement>(
		({ isIntersecting }) => {
			if (isIntersecting) {
				props.onIntersecting();
			}
		},
		[props.onIntersecting]
	);

	if (!props.files || props.files.length === 0) {
		return (
			<Drawer.Body css={{ width: "100%", height: "100%" }}>
				<Column verticalAlign="center" horizonAlign="center" css={{ width: "100%", height: "100%" }}>
					<Icon name="ToastError24" fill="#000" size={52} />
					<Typography typoSize="Body3" typoColor="neutral_70_100" align="center">
						{_P("검색 결과가 없습니다.<br />다른 검색어를 입력해주세요.")}
					</Typography>
				</Column>
			</Drawer.Body>
		);
	}

	return (
		<Drawer.Body css={{ width: "100%", height: "100%" }}>
			<Grid.ul
				css={{ width: "100%", overflowY: "auto", overflowX: "hidden", padding: "8px 60px" }}
				ref={targetRef}
				repeatSize={{ column: "216px" }}
				gridGap={12}
				column={column}
			>
				{props.files.map((file, index) => {
					return (
						<li key={file.id} ref={index === props.files!.length - 10 ? observerRef : null}>
							<FileCard file={file} searchText={props.searchText} />
						</li>
					);
				})}
			</Grid.ul>
		</Drawer.Body>
	);
}
